import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { LegacyPackhousesResponse } from './api'
import { openableRow } from '../../lib/openableRow'
import styles from './GodMode.module.css'

// The old system's clients, moved to CONTROL one at a time: each is created
// from its packhouse (or an existing client is linked to it on its own page),
// so the tags it printed with its packhouse ID stay its own.
export function LegacyPage() {
  const { user } = useAuth()
  const [data, setData] = useState<LegacyPackhousesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [state, setState] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    let live = true
    apiJson<LegacyPackhousesResponse>(user, '/api/god-mode/legacy-packhouses')
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [user])

  const moved = data?.packhouses.filter((p) => p.client).length ?? 0
  const movable = data?.packhouses.filter((p) => p.packhouse_id >= 0).length ?? 0
  const q = search.trim().toLowerCase()
  const shown = (data?.packhouses ?? []).filter(
    (p) =>
      (!q || p.name.toLowerCase().includes(q) || String(p.packhouse_id).includes(q) || p.tag_suffix?.includes(q)) &&
      (!state || (state === 'moved') === !!p.client),
  )
  // A row opens the client it moved to, or starts moving it.
  const target = (p: (typeof shown)[number]) =>
    p.client ? `/god-mode/clients/${p.client.id}` : p.packhouse_id >= 0 ? `/god-mode/new?legacy=${p.packhouse_id}` : null

  return (
    <>
      <PageHeader title="Legacy clients" />
      <PageBody>
        <div className={styles.stack}>
          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <>
              <p className={styles.lead}>
                {moved} of {movable} moved to CONTROL. Importing a client's old records waits on the copy of the legacy database.
              </p>
              <div className={styles.inline}>
                <label className={styles.field}>
                  Search
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, packhouse ID or tag suffix" />
                </label>
                <label className={styles.field}>
                  CONTROL client
                  <select value={state} onChange={(e) => setState(e.target.value)}>
                    <option value="">All</option>
                    <option value="moved">Moved</option>
                    <option value="waiting">Not moved yet</option>
                  </select>
                </label>
              </div>
              <div className={styles.tableCard}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Packhouse ID</th>
                      <th>Name</th>
                      <th>Tag suffix</th>
                      <th>CONTROL client</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.length === 0 && (
                      <tr>
                        <td colSpan={4}>No legacy client matches “{search.trim()}”.</td>
                      </tr>
                    )}
                    {shown.map((p) => (
                      <tr
                        key={p.packhouse_id}
                        className={target(p) ? styles.rowLink : undefined}
                        {...(target(p) ? openableRow(() => navigate(target(p)!)) : {})}
                        onClick={(e) => {
                          const to = target(p)
                          if (to && !(e.target as HTMLElement).closest('a')) navigate(to)
                        }}
                      >
                        <td>{p.packhouse_id}</td>
                        <td>{p.name}</td>
                        <td>{p.packhouse_id >= 0 ? <code>{p.tag_suffix}</code> : '—'}</td>
                        <td>
                          {p.client ? (
                            <Link to={`/god-mode/clients/${p.client.id}`}>{p.client.name}</Link>
                          ) : p.packhouse_id < 0 ? (
                            'Carries no tags'
                          ) : (
                            <Link to={`/god-mode/new?legacy=${p.packhouse_id}`} className={styles.secondary}>
                              Migrate to V4
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={styles.lead}>
                A client's tags end in its packhouse ID (9001 is <code>::09000001</code>), so tags the old system printed stay that
                client's. Until a packhouse has moved, any CONTROL client scanning its tags is told they are someone else's.
              </p>
            </>
          )}
        </div>
      </PageBody>
    </>
  )
}
